import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import Sidebar from "../../Components/Sidebar/Sidebar";
import LabelHeader from "../../Components/Commons/LabelHeader";
import TextField from "../../Components/Commons/Forms/TextField";
import BtnSave from "../../Components/Commons/Buttons/BtnSave";
import { Radio } from "antd";
import { toast } from "react-toastify";
import { checkPhilhealthIsRegistered, checkPxIsPhilClinicRegistered /*, checkPhilhealthATC */ } from "../../Components/Helpers/API/PhilhealthApi/PhilhealthApi"; // checkPhilhealthATC commented out - ATC not needed per client
import { fetchPatientSearch } from "../../Components/Helpers/API/PatientApi/PatientApi";
import { getMonths } from "../../Components/Helpers/Utilities/Common";

function IsRegisteredCheck() {
  const navigate = useNavigate();
  const [phicPin, setPhicPin] = useState("");
  const [membership, setMembership] = useState("member");
  const [loading, setLoading] = useState(false);
  // const [atc, setAtc] = useState(""); // Commented out - ATC not needed per client

  // Helper function to parse registration response
  const parseRegistrationResponse = (res) => {
    let registered;
    const d = res?.data;
    let candidate;
    if (Array.isArray(d)) {
      const item = d[0] || {};
      candidate =
        item?.isMemberDependentRegistered ??
        item?.is_patient ??
        item?.is_registered ??
        item?.status ??
        item?.message ??
        item?.result;
    } else {
      candidate =
        (typeof d === "string" ? d : undefined) ??
        d?.isMemberDependentRegistered ??
        d?.is_patient ??
        d?.data?.isMemberDependentRegistered ??
        d?.data?.is_registered ??
        d?.is_registered ??
        d?.data?.status ??
        d?.status ??
        d?.data?.message ??
        d?.message ??
        d?.result;
    }
    if (typeof candidate === "string") {
      const v = candidate.toUpperCase();
      registered = v === "YES" || v.includes("YES") ? true : v === "NO" || v.includes("NO") ? false : undefined;
    } else if (typeof candidate === "boolean") {
      registered = candidate;
    } else if (typeof candidate === "number") {
      registered = candidate === 1;
    }
    return registered;
  };

  // Helper function to extract patient details from PhilHealth or clinic response for autofill
  const extractPatientDetails = (res, isPhilHealthResponse = false) => {
    console.log('Full response object:', res);
    console.log('res.status:', res?.status);
    const d = res?.data;
    console.log('Raw response data (res.data):', d);
    console.log('d.data:', d?.data);
    const data = Array.isArray(d) ? d[0] || {} : d?.data || d || {};
    console.log('Final extracted patient data:', data);
    
    // PhilHealth API may return fields with phic_ prefix or without
    return {
      // Patient ID (for existing patients)
      patient_id: data?.id || data?.patient_id || null,
      master_patient_id: data?.master_patient_id || data?.patient_master_id || null,
      is_fpe: data?.is_fpe || false,
      
      // Basic patient information
      last_name: data?.last_name || data?.lastName || data?.phic_last_name || "",
      first_name: data?.first_name || data?.firstName || data?.phic_first_name || "",
      middle_name: data?.middle_name || data?.middleName || data?.phic_middle_name || "",
      suffix: data?.suffix || data?.extension_name || data?.phic_extension_name || "",
      birthdate: data?.birthdate || data?.birth_date || data?.phic_birthdate || "",
      contact_no: data?.contact_no || data?.mobile_number || data?.phic_contact_no || "",
      email: data?.email || "",
      sex: (data?.sex || data?.gender || "").toLowerCase(),
      
      // Address fields
      country: data?.country || "PHILIPPINES",
      province: data?.province || "",
      city: data?.city || "",
      barangay: data?.barangay || "",
      street: data?.street || "",
      zip_code: data?.zip_code || "",
      
      // Civil status and other demographics
      civil_status: (data?.civil_status || data?.status || "").toLowerCase(),
      occupation: data?.occupation || "",
      nationality: data?.nationality || "FILIPINO",
      religion: data?.religion || "",
      
      // Emergency contact
      emergency_contact: data?.emergency_contact || "",
      emergency_contact_no: data?.emergency_contact_no || "",
      relation_w_contact: data?.relation_w_contact || "",
      
      // Senior/PWD IDs
      senior_id: data?.senior_id || "",
      pwd_id: data?.pwd_id || "",
      is_pwd: data?.pwd_id || data?.is_pwd === "1" || data?.is_pwd === 1 ? "1" : "0",
      
      // PhilHealth fields
      // If membership type is member, autofill phic_pin; if dependent, leave phic_pin empty (member PIN should be entered manually)
      phic_pin: membership === "member" ? phicPin : (data?.phic_pin || ""),
      phic_membership_type: membership,
      phic_enlistment_date: data?.enlistment_date || data?.phic_enlistment_date || "",
      phic_enlistment_status: data?.enlistment_status || data?.phic_enlistment_status || "active",
      phic_package_type: data?.package_type || data?.phic_package_type || "Konsulta Package",
      phic_atc: data?.atc || data?.phic_atc || "WALKEDIN", // atc state removed - default to WALKEDIN
      
      // PhilHealth member/dependent details
      // If membership type is dependent, autofill phic_dependent_pin with the entered phicPin
      phic_dependent_pin: data?.phic_dependent_pin || (membership === "dependent" ? phicPin : ""),
      phic_last_name: data?.phic_last_name || data?.last_name || "",
      phic_first_name: data?.phic_first_name || data?.first_name || "",
      phic_middle_name: data?.phic_middle_name || data?.middle_name || "",
      phic_extension_name: data?.phic_extension_name || data?.suffix || "",
      phic_birthdate: data?.phic_birthdate || data?.birthdate || "",
      phic_contact_no: data?.phic_contact_no || data?.contact_no || "",
    };
  };

  const handleSearch = async () => {
    setLoading(true);
    // Only set phic_pin in localStorage for members; for dependents, set phic_dependent_pin instead
    if (membership === "member") {
      localStorage.setItem("phic_pin", phicPin);
      localStorage.removeItem("phic_dependent_pin");
    } else {
      localStorage.setItem("phic_dependent_pin", phicPin);
      localStorage.removeItem("phic_pin");
    }
    localStorage.setItem("phic_membership_type", membership);
    const code = membership === "member" ? "MM" : "DD";

    // Step 1: Check PhilHealth registration
    const philhealthRes = await checkPhilhealthIsRegistered(phicPin, code);
    const philhealthRegistered = philhealthRes && philhealthRes.status === 200 
      ? parseRegistrationResponse(philhealthRes) 
      : false;

    // Step 2: Check if patient is registered in PhilHealth Clinic (always call regardless of PhilHealth result)
    const clinicRes = await checkPxIsPhilClinicRegistered(phicPin, code);
    const clinicRegistered = clinicRes && clinicRes.status === 200 
      ? parseRegistrationResponse(clinicRes) 
      : false;

    // Step 3: Check ATC if provided - Commented out per client request (ATC not needed)
    // let atcRes = null;
    // if (atc) {
    //   atcRes = await checkPhilhealthATC(atc);
    // }

    // Scenario 1: PhilHealth YES + Clinic YES → Patient exists, go directly to Vital Signs
    if (philhealthRegistered === true && clinicRegistered === true) {
      toast.success("Patient verified. Redirecting to Vital Signs...");
      const patientDetails = extractPatientDetails(clinicRes);
      
      // If patient_id exists, go directly to vital signs
      if (patientDetails.patient_id) {
        navigate("/patients/vitals/" + patientDetails.patient_id, {
          state: {
            patient_sex: patientDetails.sex,
            is_fpe: patientDetails.is_fpe,
            months: getMonths(patientDetails.birthdate),
            fromPhilhealthCheck: true, // This will trigger the doctor queue modal after vitals submission
            // atc: atc, // Commented out - ATC not needed per client
          },
        });
      } else {
        // Fallback: if no patient_id, go to Add Patient form
        navigate("/patients/add/new-patient", {
          state: { 
            phic_pin: membership === "member" ? phicPin : "", 
            phic_dependent_pin: membership === "dependent" ? phicPin : "",
            phic_membership_type: membership,
            autofillData: patientDetails,
            // atc: atc, // Commented out - ATC not needed per client
          },
        });
      }
    }
    // Scenario 2: PhilHealth YES + Clinic NO, Go to Add Patient with autofill from PhilHealth
    else if (philhealthRegistered === true && clinicRegistered === false) {
      toast.info("Patient is registered to PhilHealth but not in Clinic.");
      const patientDetails = extractPatientDetails(philhealthRes, true);
      navigate("/patients/add/new-patient", {
        state: {
          phic_pin: membership === "member" ? phicPin : "",
          phic_dependent_pin: membership === "dependent" ? phicPin : "",
          phic_membership_type: membership,
          autofillData: patientDetails,
          fromPhilhealthRegistration: true,
        },
      });
    }
    // Scenario 3: PhilHealth NO + Clinic YES → Go to Add Patient with autofill from clinic
    else if (philhealthRegistered === false && clinicRegistered === true) {
      toast.info("Patient is registered in Clinic but not in PhilHealth.");
      const patientDetails = extractPatientDetails(clinicRes);
      console.log('Navigating with autofillData:', patientDetails);
      navigate("/patients/add/new-patient", {
        state: { 
          phic_pin: membership === "member" ? phicPin : "", 
          phic_dependent_pin: membership === "dependent" ? phicPin : "",
          phic_membership_type: membership,
          autofillData: patientDetails,
          fromClinicRegistration: true,
        },
      });
    }
    // Scenario 4: PhilHealth NO + Clinic NO → Stay on screen, show error
    else {
      toast.error("Patient is NOT registered both in Philhealth and Clinic. Please check the PIN and try again.");
      setLoading(false);
      // Stay on IsRegisteredCheck screen to allow user to try again
      return;
    }

    setLoading(false);
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="add-customer col p-4 pt-4 overflow-scroll">
        <LabelHeader text={"PHILHEALTH REGISTRATION CHECK"} />
        <div className="pt-2 ml-7">
          <div className="row">
            <div className="col-sm-6">
              <TextField
                required
                id="phic_pin"
                name="phic_pin"
                formLabel={`PhilHealth Identification Number (PIN)`}
                size={`sm`}
                value={phicPin}
                onChange={(e) => setPhicPin(e.target.value)}
                maxLength={12}
              />
            </div>
            <div className="col-sm-6">
              <div className="row">
                <span>Membership</span>
                <Radio.Group
                  value={membership}
                  onChange={(e) => setMembership(e.target.value)}
                >
                  <Radio value="member">Member (MM)</Radio>
                  <Radio value="dependent">Dependent (DD)</Radio>
                </Radio.Group>
              </div>
            </div>
          </div>
          {/* ATC input field - Commented out per client request (ATC not needed) */}
          {/* <div className="row mt-3">
            <div className="col-sm-6">
              <TextField
                id="atc"
                name="atc"
                formLabel={`Authorization Transaction Code (ATC)`}
                size={`sm`}
                value={atc}
                onChange={(e) => setAtc(e.target.value)}
              />
            </div>
          </div> */}
          <div className="row justify-content-end mt-5 mb-3">
            <div className="col-2">
              <BtnSave
                label={"Search"}
                size={"sm"}
                onClick={handleSearch}
                isClicked={loading}
                disabled={!phicPin || phicPin.length !== 12 || !membership}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IsRegisteredCheck;
